import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { join, map, size } from 'lodash';
import React, { useState } from 'react';

import { MarkerNormalizationGapGroup } from './helpers';

export const GAP_SLIDES_PAGE_SIZE = 10;

export interface MarkerNormalizationGapAlertProps {
  gapGroups: MarkerNormalizationGapGroup[];
  getMarkerLabel: (markerId: string) => string;
  getSlideLabel: (slideId: string) => string;
}

const GapGroupSlides: React.FC<{ slideIds: string[]; getSlideLabel: (slideId: string) => string }> = ({
  slideIds,
  getSlideLabel,
}) => {
  const [page, setPage] = useState(0);

  if (size(slideIds) <= GAP_SLIDES_PAGE_SIZE) {
    return (
      <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2.5 }}>
        {map(slideIds, (slideId) => (
          <li key={slideId}>{getSlideLabel(slideId)}</li>
        ))}
      </Typography>
    );
  }

  const pageSlideIds = slideIds.slice(page * GAP_SLIDES_PAGE_SIZE, (page + 1) * GAP_SLIDES_PAGE_SIZE);

  return (
    <TableContainer data-testid="binary-classifier-normalization-gap-slides-table">
      <Table size="small" aria-label="Slides missing normalization">
        <TableBody>
          {map(pageSlideIds, (slideId) => (
            <TableRow key={slideId}>
              <TableCell sx={{ px: 1 }}>{getSlideLabel(slideId)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={size(slideIds)}
        page={page}
        onPageChange={(_event, newPage) => setPage(newPage)}
        rowsPerPage={GAP_SLIDES_PAGE_SIZE}
        rowsPerPageOptions={[GAP_SLIDES_PAGE_SIZE]}
      />
    </TableContainer>
  );
};

export const MarkerNormalizationGapAlert: React.FC<MarkerNormalizationGapAlertProps> = ({
  gapGroups,
  getMarkerLabel,
  getSlideLabel,
}) => {
  if (size(gapGroups) === 0) {
    return null;
  }

  return (
    <Alert severity="warning" data-testid="binary-classifier-normalization-gap-alert">
      <AlertTitle>{"Some markers can't be classified"}</AlertTitle>
      <Typography variant="body2">
        The binary classifier only runs on markers that are normalized on every selected slide. Normalize these markers
        in Marker QC to include them.
      </Typography>
      <Stack spacing={1.5} sx={{ mt: 1.5, maxHeight: 280, overflowY: 'auto' }}>
        {map(gapGroups, (gapGroup) => (
          <Box key={join(gapGroup.markerIds, '|')} data-testid="binary-classifier-normalization-gap-group">
            <Typography variant="caption" color="text.secondary" component="div">
              {`Missing normalization on ${size(gapGroup.slideIds)} ${
                size(gapGroup.slideIds) === 1 ? 'slide' : 'slides'
              }`}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ my: 0.5 }}>
              {map(gapGroup.markerIds, (markerId) => (
                <Chip key={markerId} size="small" label={getMarkerLabel(markerId)} />
              ))}
            </Stack>
            <GapGroupSlides slideIds={gapGroup.slideIds} getSlideLabel={getSlideLabel} />
          </Box>
        ))}
      </Stack>
    </Alert>
  );
};
